import { OUTBOUND_CANCELLED_STATUSES } from './entry.constants';
import { OutboundOrderDetailItem, OutboundRequestTag } from './entry.types';
import { isEmptyProductRow } from './entry.utils';

export function normalizeOutboundRequestId(raw: string): string {
  return raw.trim().replace(/^#/, '');
}

export function parseOutboundRequestIdsStored(value: unknown): string[] {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map(id => normalizeOutboundRequestId(String(id))).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(id => normalizeOutboundRequestId(id))
      .filter(Boolean);
  }
  return [];
}

export function formatOutboundRequestIdsForApi(tags: OutboundRequestTag[]): string {
  return tags.map(tag => tag.id).join(',');
}

export function getOutboundItemSku(item: OutboundOrderDetailItem): string {
  return (item.code || '').trim();
}

function getOutboundItemUids(item: any): string[] {
  if (!item?.uids?.length) {
    return [];
  }
  return item.uids
    .map((uidItem: { uid?: string; productSerial?: string } | string) => {
      if (typeof uidItem === 'string') {
        return uidItem;
      }
      return uidItem.uid || uidItem.productSerial || '';
    })
    .filter(Boolean);
}

function normalizeOrderRequestItem(item: any): OutboundOrderDetailItem {
  const productInfo = item?.productInfo || {};
  const uidList = getOutboundItemUids(item);

  return {
    code: productInfo.code || item.code || '',
    name: productInfo.name || item.name || '',
    quantity: item.quantity ?? 1,
    color: item.color || undefined,
    note: item.note || undefined,
    uid: uidList.length ? uidList.join(', ') : undefined,
    productId: productInfo.id,
    vat: productInfo.vat,
    minPrice: productInfo.minPrice,
    maxPrice: productInfo.maxPrice,
  };
}

export function parseOutboundOrderDetailsResponse(data: any): {
  items: OutboundOrderDetailItem[];
  status: 'valid' | 'cancelled';
  displayId?: number;
} {
  const rawItems: any[] = Array.isArray(data) ? data : data?.items || data?.products || data?.orderItems || [];
  const statusValue = data?.status;
  const isCancelled =
    OUTBOUND_CANCELLED_STATUSES.includes(statusValue) ||
    statusValue === 'cancelled' ||
    statusValue === 'canceled' ||
    data?.cancelled === true ||
    data?.isCancelled === true;

  return {
    items: rawItems.map(normalizeOrderRequestItem),
    status: isCancelled ? 'cancelled' : 'valid',
    displayId: data?.displayId,
  };
}

export function parseOutboundTagsFromOrder(data: any): OutboundRequestTag[] {
  const outboundRequestIdList = parseOutboundRequestIdsStored(data.outboundRequestId ?? data.outboundRequestIds);
  return outboundRequestIdList.map((id: string) => ({
    id,
    status: data.outboundRequestStatuses?.[id] === 'cancelled' ? 'cancelled' : 'valid',
  }));
}

export function mergeImportedProducts<T extends { code?: string; productId?: number; name?: string }>(
  currentProducts: T[],
  newProducts: T[],
): T[] {
  const filledProducts = currentProducts.filter(product => !isEmptyProductRow(product));
  return [...filledProducts, ...newProducts];
}
